/**
 * useIntegrationHealth
 *
 * Hook genérico multi-provider para consultar saúde das integrações
 * externas do tenant (camada `integration_health`).
 *
 * Reutilizável por qualquer módulo (financial, messaging, calendar, signature, ...).
 *
 * RELEASE: 2026-06-17 v1.0.0 - Versão inicial.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useIntegrationHealth');

export type IntegrationHealthStatus =
  | 'healthy'
  | 'degraded'
  | 'failed'
  | 'not_configured'
  | 'unknown';

export type IntegrationMode =
  | 'fully_operational'
  | 'usable_with_warning'
  | 'down';

export interface IntegrationHealthRow {
  id: string;
  module: string;
  provider_slug: string;
  credential_id: string | null;
  resource_kind: string;
  status: IntegrationHealthStatus;
  integration_mode: IntegrationMode;
  last_check_at: string | null;
  last_success_at: string | null;
  failure_count: number;
  last_error: string | null;
  metadata: Record<string, unknown>;
}

/**
 * Busca a saúde das integrações do tenant ativo. Filtro opcional por módulo.
 */
export function useIntegrationHealth(module?: string) {
  return useQuery({
    queryKey: ['integration-health', module ?? 'all'],
    queryFn: async (): Promise<IntegrationHealthRow[]> => {
      const { data, error } = await supabase.rpc('get_integration_health', {
        p_module: module ?? null,
      });
      if (error) {
        logger.error('Falha ao buscar saúde de integrações', { error: error.message });
        throw error;
      }
      return (data || []) as IntegrationHealthRow[];
    },
    // Polling moderado — o cron */5min faz o trabalho pesado; só refletimos no UI.
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}
